import { useState, useEffect, useCallback, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { cn } from '@utils'
import { useSessionStore } from '@stores'
import { useUIStore } from '@stores'
import { SessionStatus, InputState } from '@/constants'
import { authService } from '@services/authService'
import { cartService } from '@services/cartService'
import StepTitle from './StepTitle'
import { ArrowRight } from 'lucide-react'
import { CustomerMatchMessages } from '@/constants/loadingMessages'
import { emitProcessingMessages, clearProcessingMessages } from '@/utils/processing'
import { stopMouseflowRecording } from '@/utils/mouseflow'

interface CustomerMatchProps {
  getCart?: boolean
  setGlobalLoading?: boolean
  onDone?: () => void
  onFail?: (message: string) => void
  onBack?: () => void
  onVerifying?: (verifying: boolean) => void
}

type MatchField = 'email' | 'phone' | 'order_number'

const FIELD_CONFIG: Record<MatchField, { label: string; type: string; placeholder: string }> = {
  email: {
    label: 'Email Address',
    type: 'email',
    placeholder: 'example@gmail.com',
  },
  phone: { label: 'Phone Number', type: 'tel', placeholder: '555-555-5555' },
  order_number: { label: 'Order Number', type: 'text', placeholder: '123456' },
}

const matchSchema = z
  .object({
    email: z.string().optional(),
    phone: z.string().optional(),
    order_number: z.string().optional(),
  })
  .refine(data => Object.values(data).some(v => v && v.trim().length > 0), {
    message: 'Please fill at least one field to continue',
  })

type MatchFormValues = z.infer<typeof matchSchema>

const CustomerMatch = ({
  getCart = true,
  setGlobalLoading = true,
  onDone,
  onFail,
  onBack,
  onVerifying,
}: CustomerMatchProps) => {
  const session = useSessionStore(state => state.session)
  const campaign = useSessionStore(state => state.campaign)
  const setInputState = useSessionStore(state => state.setInputState)
  const setSession = useSessionStore(state => state.setSession)
  const setMatchVerifyError = useSessionStore(state => state.setMatchVerifyError)
  const matchVerifyErrors = useSessionStore(state => state.isMatchVerifyError)
  const setLoading = useUIStore(s => s.setLoading)
  const setCriticalError = useUIStore(s => s.setCriticalError)

  const [retryCnt, setRetryCnt] = useState(0)
  const [fieldErrorCounts, setFieldErrorCounts] = useState<Record<string, number>>({})
  const [highlightedFields, setHighlightedFields] = useState<string[]>([])

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { isSubmitting, isValid, errors },
  } = useForm<MatchFormValues>({
    resolver: zodResolver(matchSchema),
    mode: 'onChange',
    defaultValues: { email: '', phone: '', order_number: '' },
  })

  const values = watch()
  const hasAnyValue = Object.values(values).some(v => v && v.trim().length > 0)

  const matchFields: MatchField[] = useMemo(() => (campaign as any)?.match_fields ?? [], [campaign])

  useEffect(() => {
    reset({ email: '', phone: '', order_number: '' })
    setFieldErrorCounts({})
    setHighlightedFields([])
  }, [campaign, reset])

  useEffect(() => {
    stopMouseflowRecording()
    return () => {
      clearProcessingMessages()
      setMatchVerifyError([])
    }
  }, [])

  const getFieldsString = useCallback(
    (fields: string[]): string =>
      fields.reduce((acc, field, i) => {
        const label = FIELD_CONFIG[field as MatchField]?.label.toLowerCase() ?? field.replace('_', ' ')
        if (i === 0) {
          return label
        }
        return acc + (i === fields.length - 1 ? ' or ' : ', ') + label
      }, ''),
    []
  )

  const buildRetryHint = useCallback(
    (formValues: MatchFormValues, currentRetryCnt: number) => {
      if (!matchFields.length) {
        return ''
      }
      const counts = { ...fieldErrorCounts }
      const filled: string[] = []
      const empty: string[] = []

      matchFields.forEach(field => {
        const val = (formValues[field] || '').trim()
        if (val) {
          counts[field] = (counts[field] ?? 0) + 1
          filled.push(field)
        } else if (!counts[field]) {
          empty.push(field)
        }
      })
      setFieldErrorCounts(counts)

      let msg = ''
      let highlighted: string[] = []
      if (currentRetryCnt === 1 || empty.length === 0) {
        msg += `<br/>Make sure you typed your ${getFieldsString(filled)} correctly.`
        highlighted = filled
      }
      if (empty.length > 1) {
        if (currentRetryCnt > 1) {
          reset({ email: '', phone: '', order_number: '' })
          msg += ' Try'
        } else {
          msg += ' Or try'
        }
        msg += ` entering your ${getFieldsString(empty)} instead.`
        highlighted = [...highlighted, ...empty]
      }
      setHighlightedFields(highlighted)
      return msg + " And make sure it's the same as when you placed your order."
    },
    [fieldErrorCounts, matchFields, getFieldsString, reset]
  )

  const processPostMatch = async (sessionData: any) => {
    // Update session store with the match result so Session.tsx has the updated status
    setSession(sessionData)

    if (getCart && sessionData.customer) {
      try {
        await cartService.getCart()
      } catch {
        /* ignore */
      }
    }

    // When global loading is active and session needs verification, set input state
    if (
      setGlobalLoading &&
      (sessionData.status === SessionStatus.Unverified || sessionData.status === SessionStatus.Matched)
    ) {
      setInputState(InputState.NeedsVerification)
      // Clear loading so CustomerVerify can be rendered
      clearProcessingMessages()
      setLoading(false)
    }

    onDone?.()
    if (!setGlobalLoading) {
      onVerifying?.(false)
    }
  }

  const onSubmit = async (formValues: MatchFormValues) => {
    if (!session || !matchFields.length) {
      return
    }
    setMatchVerifyError([])
    const params: Record<string, string> = {}
    matchFields.forEach(f => {
      params[f] = (formValues[f] || '').trim()
    })

    if (!setGlobalLoading) {
      onVerifying?.(true)
    } else {
      setLoading(true)
    }
    emitProcessingMessages(CustomerMatchMessages)

    try {
      const result = await authService.match(session.id, params)
      await processPostMatch(result)
    } catch (err: any) {
      clearProcessingMessages()
      if (setGlobalLoading) {
        setLoading(false)
      }
      const newRetryCnt = retryCnt + 1
      setRetryCnt(newRetryCnt)
      const error = err?.originalError || err

      if (error?.response?.status === 404) {
        const message = "Sorry, but we couldn't find your information."
        if (newRetryCnt < (session as any).lifeline_unmatched_count) {
          setMatchVerifyError([message + buildRetryHint(formValues, newRetryCnt)])
        } else {
          onFail?.(message)
        }
      } else if (error?.response?.status === 403) {
        const message = error.response.data?.message || "Sorry, but we couldn't find your information."
        onFail?.(message)
      } else if (error?.response?.status === 400) {
        const errs: string[] = []
        Object.keys(error.response.data).forEach(k => errs.push(...error.response.data[k]))
        setMatchVerifyError(errs)
      } else {
        setCriticalError(error)
        reset({ email: '', phone: '', order_number: '' })
        setFieldErrorCounts({})
        setHighlightedFields([])
      }

      if (!setGlobalLoading) {
        onVerifying?.(false)
      }
    }
  }

  if (!session || !campaign) {
    return null
  }

  return (
    <div className="w-full max-w-[560px] mx-auto px-2 py-6 lg:px-4">
      <StepTitle subtitle={<div className="subtitle">Complete one field to continue</div>}>
        <div dangerouslySetInnerHTML={{ __html: (session as any).match_message }} />
      </StepTitle>

      {matchVerifyErrors?.length > 0 && (
        <div className="match-verify-alerts mt-4 space-y-2">
          {matchVerifyErrors.map((msg: string, idx: number) => (
            <div
              key={idx}
              role="alert"
              className="rounded-md px-4 py-3 text-sm border"
              style={{
                backgroundColor: 'color-mix(in srgb, #ef4444 8%, var(--content-background-color))',
                borderColor: 'color-mix(in srgb, #ef4444 35%, transparent)',
                color: '#b91c1c',
              }}
              dangerouslySetInnerHTML={{ __html: msg }}
            />
          ))}
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-4">
        {matchFields.map((field, index) => {
          const config = FIELD_CONFIG[field]
          const fieldValue = (values[field] || '').trim()
          const showField = fieldValue || !hasAnyValue
          const highlighted = highlightedFields.includes(field)

          return (
            <div key={`field-${field}`}>
              {index > 0 && !hasAnyValue && (
                <div className="field-or-divider">
                  <span>or</span>
                </div>
              )}
              {showField && (
                <div className="w-full">
                  <label
                    htmlFor={field}
                    className="block text-base font-medium mb-1"
                    style={{ color: 'var(--text-color)' }}>
                    {config.label}
                  </label>
                  <input
                    id={field}
                    type={config.type}
                    placeholder={config.placeholder}
                    {...register(field)}
                    className={cn('form-control w-full', highlighted && 'border-red-500! focus:border-red-500!')}
                  />
                </div>
              )}
            </div>
          )
        })}

        {!hasAnyValue && errors.root && <p className="text-sm text-red-500">{errors.root.message}</p>}

        <div className="flex justify-center gap-3 pt-2">
          {onBack && (
            <button type="button" onClick={() => onBack()} className="btn-base back-btn w-1/2!">
              Back
            </button>
          )}
          <button
            type="submit"
            disabled={isSubmitting || !isValid}
            className={cn(
              'btn-base input-choice-btn input-btn flex! items-center justify-center gap-2',
              onBack ? 'w-1/2!' : 'w-full'
            )}>
            {isSubmitting ? (
              <span className="btn-spinner" />
            ) : setGlobalLoading ? (
              'Submit'
            ) : (
              <>
                <span>Submit &amp; Verify</span>
                <ArrowRight size={18} />
              </>
            )}
          </button>
        </div>
      </form>

      {session.is_popup && !session.can_undo && (
        <button
          type="button"
          className="block mx-auto mt-4 text-2xl leading-none text-gray-400 hover:text-gray-600"
          onClick={() => onBack?.()}
          aria-label="Close">
          &times;
        </button>
      )}
    </div>
  )
}

export default CustomerMatch
